import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Moon, AlertTriangle } from "lucide-react";
import { getDespesasGerais, getRoleFromToken } from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRow } from "../../components/dashboard/KpiCard";
import {
  MESES_ABREV, formatCurrency,
  calcValorMensalBase, calcProjecaoMensal, calcTotalAnual, somarProjecoes,
} from "../../utils/orcamento";
import { departamentosVisiveis } from "../../constants/departamentosOrcamento";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária)
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = Array.from({ length: ANO_ATUAL + 2 - 2025 + 1 }, (_, i) => 2025 + i);

// tipo_lancamento='viagem' ou modelo_valor='faixas': o backend já manda valores_mensais prontos
// — calcProjecaoMensal só sabe ler itens 'legado' (valor_atual/qtde_usuario_atual/reajustes/
// expansoes, vazios/default nesses itens). Mesmo branch de calcularItensComCalculo em
// GestaoOrcamentariaView.js — sem isso, esses itens somariam R$0 nesta visão consolidada.
function calcProjecaoItem(item) {
  if (item.tipo_lancamento === "viagem" || item.modelo_valor === "faixas") {
    return Array.from({ length: 12 }, (_, i) => Number(item.valores_mensais?.[i + 1] || 0));
  }
  return calcProjecaoMensal(item);
}

function calcValorBaseItem(item) {
  if (item.tipo_lancamento === "viagem" || item.modelo_valor === "faixas") return 0;
  return calcValorMensalBase(item);
}

export default function GeralOrcamento() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const role = getRoleFromToken();
  const departamentosAcessiveis = useMemo(() => departamentosVisiveis(role), [role]);

  const [ano, setAno] = useState(ANO_ATUAL+1);
  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  useEffect(() => {
    async function carregar() {
      setLoading(true);
      setErro("");
      try {
        const its = await getDespesasGerais(ano); // sem departamento => backend devolve todos os visíveis para a role
        setItens(its || []);
      } catch (e) {
        setErro(e.message);
      } finally {
        setLoading(false);
      }
    }
    carregar();
  }, [ano]);

  const linhasPorDepartamento = useMemo(() => departamentosAcessiveis.map((departamento) => {
    const itensDep = itens.filter((i) => i.departamento === departamento);
    const projecao = somarProjecoes(itensDep.map((i) => calcProjecaoItem(i)));
    const valorAtual = itensDep.reduce((acc, i) => acc + calcValorBaseItem(i), 0);
    return { departamento, itens: itensDep, projecao, total: calcTotalAnual(projecao), valorAtual };
  }), [departamentosAcessiveis, itens]);

  const totalGeralProjecao = useMemo(() => somarProjecoes(linhasPorDepartamento.map((l) => l.projecao)), [linhasPorDepartamento]);
  const totalOrcadoBase = linhasPorDepartamento.reduce((acc, l) => acc + l.valorAtual, 0);
  const totalProjetadoAno = calcTotalAnual(totalGeralProjecao);

  const kpiItems = [
    { label: "Total Orçado (Base)", value: loading ? "—" : formatCurrency(totalOrcadoBase), color: C.brand },
    { label: "Total Projetado no Ano", value: loading ? "—" : formatCurrency(totalProjetadoAno), color: C.success },
    { label: "Departamentos", value: loading ? "—" : String(departamentosAcessiveis.length), color: C.text },
    { label: "Itens Considerados", value: loading ? "—" : String(itens.length), color: C.text },
  ];

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Geral</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRow items={kpiItems} C={C} />
          </section>

          <section>
            <h2 style={{ margin: "0 0 12px", fontSize: 14, fontWeight: 700, color: C.text }}>
              Visão Geral · Despesas e Custos por Departamento · {ano}
            </h2>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : departamentosAcessiveis.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum departamento disponível para o seu perfil.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1400 }}>
                    <thead>
                      <tr>
                        <th style={{ ...thStyle(C), textAlign: "left" }}>Departamento</th>
                        <th style={thStyle(C)}>Valor Atual (Base)</th>
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                        <th style={thStyle(C)}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linhasPorDepartamento.map((linha) => (
                        <tr key={linha.departamento}>
                          <td style={{ ...tdStyle(C), textAlign: "left", fontWeight: 600 }}>
                            {linha.departamento}
                            {linha.itens.length === 0 && (
                              <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, color: C.muted }}>(sem itens)</span>
                            )}
                          </td>
                          <td style={tdStyle(C)}>{formatCurrency(linha.valorAtual)}</td>
                          {linha.projecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                          <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(linha.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ ...tdStyle(C, true), textAlign: "left" }}>Total Geral</td>
                        <td style={tdStyle(C)}>{formatCurrency(totalOrcadoBase)}</td>
                        {totalGeralProjecao.map((v, i) => <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>)}
                        <td style={tdStyle(C)}>{formatCurrency(totalProjetadoAno)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
            <p style={{ margin: "8px 0 0", fontSize: 11, color: C.muted }}>
              Soma todos os departamentos visíveis para o seu perfil. Para editar categorias e itens, use "Despesas Gerais" ou "Custos Gerais".
            </p>
          </section>
        </main>
      </div>
    </div>
  );
}

function thStyle(C) {
  return {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}
